import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { SearchIcon, DollarSignIcon, ReceiptIcon, StarIcon, GlobeIcon, UsersIcon, CheckCircleIcon, BriefcaseIcon, MapPinIcon, MailIcon, PhoneIcon, LinkedinIcon, UserIcon, SettingsIcon, BuildingIcon, ActivityIcon, NavigationIcon, CalendarIcon, MonitorIcon, LockIcon, ArrowRightIcon } from 'lucide-react';
const tabs = [{
  id: 'company-info',
  label: 'Company Info',
  fields: [{
    icon: GlobeIcon,
    label: 'Website',
    value: 'sunrisebakery.com'
  }, {
    icon: CheckCircleIcon,
    label: 'Validate Website',
    value: 'VALID - Active & Reachable'
  }, {
    icon: StarIcon,
    label: 'Review Statistics',
    value: '4.7 stars (89 reviews)'
  }, {
    icon: UsersIcon,
    label: 'Employee Count',
    value: '8-15 employees'
  }, {
    icon: BriefcaseIcon,
    label: 'Job Openings',
    value: '2 open positions'
  }, {
    icon: MapPinIcon,
    label: 'Number of Locations',
    value: '3 locations'
  }]
}, {
  id: 'owner-data',
  label: 'Owner Info',
  fields: [{
    icon: UserIcon,
    label: 'Owner Name',
    value: 'Margaret Chen'
  }, {
    icon: MailIcon,
    label: 'Owner Email',
    value: 'margaret@sunrisebakery.com'
  }, {
    icon: PhoneIcon,
    label: 'Direct Phone',
    value: '(512) 555-0342'
  }, {
    icon: LinkedinIcon,
    label: 'LinkedIn Profile',
    value: 'linkedin.com/in/mchen'
  }]
}, {
  id: 'performance',
  label: 'Performance & Financials',
  fields: [{
    icon: DollarSignIcon,
    label: 'Annual Revenue',
    value: '$850K - $1.2M'
  }, {
    icon: ReceiptIcon,
    label: 'Average Ticket Size',
    value: '$42.50'
  }, {
    icon: ActivityIcon,
    label: 'Website Traffic',
    value: '12,400 visits/mo'
  }, {
    icon: MonitorIcon,
    label: 'Technology Stack',
    value: 'Square POS, Shopify, Mailchimp'
  }]
}, {
  id: 'custom',
  label: 'Custom & Advanced',
  fields: [{
    icon: MailIcon,
    label: 'Generate Email Template',
    value: 'Hi Margaret, I noticed your bakery...'
  }, {
    icon: SettingsIcon,
    label: 'Create Your Own Enrichment',
    value: 'Custom data retrieved via Openmart bot'
  }]
}];
export function EnrichmentSearch() {
  const [query, setQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [searchedName, setSearchedName] = useState('');
  const [activeTab, setActiveTab] = useState(tabs[0].id);
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (!query.trim()) return;
    setIsLoading(true);
    setTimeout(() => {
      setSearchedName(query);
      setActiveTab(tabs[0].id); // Reset to first tab on new search
      setIsLoading(false);
    }, 800);
  };
  const displayName = searchedName || 'Sunrise Bakery';
  const activeTabData = tabs.find(t => t.id === activeTab);
  return <section className="py-20 bg-white border-b border-slate-100" data-id="element-2880">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2881">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start" data-id="element-2882">
          {/* Left: Title + Search */}
          <div className="lg:col-span-5 lg:sticky lg:top-32" data-id="element-2883">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4 leading-tight" data-id="element-2884">
              Enrich any local <span className="text-blue-600" data-id="element-2885">business</span>
            </h2>
            <p className="text-lg text-slate-600 leading-relaxed mb-8" data-id="element-2886">
              Turn a business name into 50+ verified data fields instantly.
            </p>

            <form onSubmit={handleSearch} className="mb-8" data-id="element-2887">
              <div className="flex gap-2" data-id="element-2888">
                <div className="flex-1 flex items-center bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 focus-within:border-blue-300 focus-within:ring-2 focus-within:ring-blue-100 transition-all" data-id="element-2889">
                  <SearchIcon className="w-5 h-5 text-slate-400 mr-3 shrink-0" data-id="element-2890" />
                  <input type="text" placeholder="Business name..." value={query} onChange={e => setQuery(e.target.value)} required className="w-full bg-transparent border-none text-slate-900 placeholder:text-slate-400 text-base outline-none" data-id="element-2891" />
                </div>
                <button type="submit" disabled={isLoading || !query.trim()} className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl transition-all flex items-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed shrink-0" data-id="element-2892">
                  {isLoading ? <motion.div animate={{
                  rotate: 360
                }} transition={{
                  repeat: Infinity,
                  duration: 1,
                  ease: 'linear'
                }} className="w-5 h-5 border-2 border-white border-t-transparent rounded-full" data-id="element-2893" /> : 'Search'}
                </button>
              </div>
            </form>

            <div className="bg-slate-50 rounded-2xl p-6 border border-slate-100" data-id="element-2894">
              <div className="flex items-center gap-3 mb-3" data-id="element-2895">
                <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center" data-id="element-2896">
                  <LockIcon className="w-5 h-5 text-blue-600" data-id="element-2897" />
                </div>
                <div data-id="element-2898">
                  <div className="text-sm font-bold text-slate-900" data-id="element-2899">
                    Unlock full enrichment data
                  </div>
                  <div className="text-xs text-slate-500" data-id="element-2900">
                    50+ verified fields per record
                  </div>
                </div>
              </div>
              <a href="http://app.openmart.com/register" target="_blank" rel="noopener noreferrer" className="mt-3 w-full inline-flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl transition-all text-sm" data-id="element-2901">
                Start free <ArrowRightIcon className="w-4 h-4" data-id="element-2902" />
              </a>
            </div>
          </div>

          {/* Right: Tabbed Results Card */}
          <div className="lg:col-span-7" data-id="element-2903">
            <AnimatePresence mode="wait" data-id="element-2904">
              <motion.div key={displayName} initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.4
            }} className="bg-slate-50 rounded-2xl border border-slate-200 overflow-hidden" data-id="element-2905">
                {/* Result Header */}
                <div className="bg-white px-6 py-4 border-b border-slate-200 flex items-center justify-between" data-id="element-2906">
                  <div data-id="element-2907">
                    <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2" data-id="element-2908">
                      {displayName}
                    </h3>
                  </div>
                  <div className="text-xs font-bold text-blue-600 bg-blue-50 px-3 py-1.5 rounded-full flex items-center gap-1" data-id="element-2909">
                    <LockIcon className="w-3 h-3" data-id="element-2910" />
                    50+ fields
                  </div>
                </div>

                {/* Tabs */}
                <div className="px-6 pt-4 pb-2 border-b border-slate-200 overflow-x-auto scrollbar-hide" data-id="element-2911">
                  <div className="flex items-center gap-1 min-w-max" data-id="element-2912">
                    {tabs.map(tab => <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`px-4 py-2 text-xs font-bold rounded-full transition-all ${activeTab === tab.id ? 'bg-slate-900 text-white' : 'bg-transparent text-slate-500 hover:text-slate-700 hover:bg-slate-100'}`} data-id="element-2913">
                        {tab.label}
                      </button>)}
                  </div>
                </div>

                {/* Blurred Fields */}
                <div className="p-6 min-h-[280px]" data-id="element-2914">
                  <AnimatePresence mode="wait" data-id="element-2915">
                    <motion.div key={activeTab} initial={{
                    opacity: 0,
                    y: 5
                  }} animate={{
                    opacity: 1,
                    y: 0
                  }} exit={{
                    opacity: 0,
                    y: -5
                  }} transition={{
                    duration: 0.2
                  }} className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-2916">
                      {activeTabData?.fields.map((field, idx) => {
                      const Icon = field.icon;
                      return <motion.div key={idx} initial={{
                        opacity: 0
                      }} animate={{
                        opacity: 1
                      }} transition={{
                        delay: idx * 0.04,
                        duration: 0.3
                      }} className="flex items-center gap-3 bg-white rounded-xl px-4 py-3 border border-slate-100" data-id="element-2917">
                            <Icon className="w-4 h-4 text-slate-400 shrink-0" data-id="element-2918" />
                            <div className="flex-1 min-w-0" data-id="element-2919">
                              <div className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider" data-id="element-2920">
                                {field.label}
                              </div>
                              <div className="text-sm font-medium text-slate-700 blur-[5px] select-none truncate" data-id="element-2921">
                                {field.value}
                              </div>
                            </div>
                          </motion.div>;
                    })}
                    </motion.div>
                  </AnimatePresence>

                  {/* Inline CTA */}
                  <div className="mt-6 text-center" data-id="element-2922">
                    <a href="http://app.openmart.com/register" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-sm font-bold text-blue-600 hover:text-blue-700 transition-colors" data-id="element-2923">
                      Sign up to reveal all fields{' '}
                      <ArrowRightIcon className="w-4 h-4" data-id="element-2924" />
                    </a>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>;
}